import { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarDays, Utensils, Moon, Users, Sun, IndianRupee } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl bg-white border border-sand px-4 py-3.5 text-sm text-coal placeholder:text-sage/50 outline-none transition-colors duration-300 focus:border-pine focus:ring-2 focus:ring-pine/15";
const labelCls = "font-mono text-[10px] uppercase tracking-[0.25em] text-sage";

const toYMD = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const isWeekend = (ymd) => {
  const [y, m, d] = ymd.split("-").map(Number);
  const day = new Date(y, m - 1, d).getDay();
  return day === 0 || day === 6;
};

const upcomingWeekends = (count) => {
  const out = [];
  const d = new Date();
  d.setDate(d.getDate() + 1);
  while (out.length < count) {
    if (d.getDay() === 0 || d.getDay() === 6) out.push(toYMD(new Date(d)));
    d.setDate(d.getDate() + 1);
  }
  return out;
};

const fmtChip = (ymd) => {
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return {
    dow: dt.toLocaleDateString("en-IN", { weekday: "short" }),
    day: dt.getDate(),
    mon: dt.toLocaleDateString("en-IN", { month: "short" }),
  };
};

const RULES = [
  { icon: CalendarDays, title: "Saturdays & Sundays", body: "The farm welcomes visitors on weekends only." },
  { icon: Sun, title: "Morning to evening", body: "Spend the full day — milking, grazing, the gobar gas plant and the calf shed." },
  { icon: Utensils, title: "Farm meals", body: "Fresh, simple food cooked at the farm — add it to your booking." },
  { icon: Moon, title: "Night stay", body: "Farm house for up to 4 persons — ₹3,500 per night. Book in advance." },
];

const NIGHT_STAY_PRICE = 3500;
const NIGHT_STAY_MAX = 4;

const PRICE_ROWS = [
  { id: "day", label: "Day visit", value: "Complimentary", hint: "Morning to evening · advance booking" },
  { id: "night", label: "Night stay · farm house", value: `₹${NIGHT_STAY_PRICE.toLocaleString("en-IN")}`, hint: "Per night · up to 4 persons (2 adults + 2 kids)" },
  { id: "meals", label: "Farm meals", value: "On request", hint: "Per person · quoted on confirmation call" },
  { id: "extra", label: "Extra guests (night stay)", value: "On request", hint: "Beyond 4 persons · quoted on confirmation call" },
];

const PriceCard = ({ form }) => {
  const guests = Number(form.visitors) || 0;
  const nightTotal = form.night_stay ? NIGHT_STAY_PRICE : 0;
  const extraGuests = form.night_stay && guests > NIGHT_STAY_MAX;
  return (
    <div data-testid="visit-price-card" className="rounded-2xl border border-sand bg-parchment p-5 md:p-6">
      <div className="flex items-center justify-between gap-4">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-sage">Pricing</p>
        <IndianRupee className="h-4 w-4 text-gold" />
      </div>
      <ul className="mt-4 divide-y divide-sand">
        {PRICE_ROWS.map((r) => (
          <li key={r.id} data-testid={`visit-price-row-${r.id}`} className="py-3 flex items-start justify-between gap-4">
            <div>
              <p className="text-sm text-coal">{r.label}</p>
              <p className="text-xs text-sage mt-0.5">{r.hint}</p>
            </div>
            <p className="font-serif text-lg text-pine whitespace-nowrap">{r.value}</p>
          </li>
        ))}
      </ul>
      <div className="mt-4 rounded-xl bg-white border border-sand px-4 py-3 flex items-center justify-between gap-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-sage">Your estimate</p>
          <p className="text-xs text-sage mt-0.5">
            {form.night_stay
              ? extraGuests
                ? `1 night · ${guests} guests — extra guests quoted on call`
                : `1 night · up to ${NIGHT_STAY_MAX} persons`
              : "Day visit — no charge"}
            {form.meals ? " · meals quoted on call" : ""}
          </p>
        </div>
        <p data-testid="visit-estimate-amount" className="font-serif text-2xl text-pine whitespace-nowrap">
          ₹{nightTotal.toLocaleString("en-IN")}
          {(extraGuests || form.meals) && <span className="text-gold">+</span>}
        </p>
      </div>
      <p className="mt-3 text-xs text-sage leading-relaxed">
        Payment is taken over the phone after we confirm your date. Your booking is confirmed on
        WhatsApp once payment is received.
      </p>
    </div>
  );
};

const Toggle = ({ id, icon: Icon, label, hint, checked, onChange }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    data-testid={`visit-toggle-${id}`}
    onClick={() => onChange(!checked)}
    className={`flex items-center gap-4 rounded-2xl border px-5 py-4 text-left transition-colors duration-300 ${
      checked ? "border-pine bg-pine text-cream" : "border-sand bg-white text-coal hover:border-pine/50"
    }`}
  >
    <Icon className={`h-5 w-5 shrink-0 ${checked ? "text-gold" : "text-sage"}`} />
    <span className="grow">
      <span className="block text-sm font-medium">{label}</span>
      <span className={`block text-xs mt-0.5 ${checked ? "text-cream/70" : "text-sage"}`}>{hint}</span>
    </span>
    <span
      className={`relative h-6 w-11 shrink-0 rounded-full transition-colors duration-300 ${
        checked ? "bg-gold" : "bg-sand"
      }`}
    >
      <span
        className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform duration-300 ${
          checked ? "translate-x-5" : "translate-x-0.5"
        }`}
      />
    </span>
  </button>
);

const initial = {
  name: "",
  phone: "",
  email: "",
  visit_date: "",
  visitors: 2,
  meals: false,
  night_stay: false,
  notes: "",
};

export default function Visit() {
  const { user, openAuth } = useAuth();
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);
  const weekends = useMemo(() => upcomingWeekends(8), []);
  const minDate = weekends[0];

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const setVal = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const dateError =
    form.visit_date && !isWeekend(form.visit_date)
      ? "Farm visits are on Saturdays and Sundays only."
      : form.visit_date && form.visit_date < minDate
      ? "Advance booking is compulsory — pick an upcoming weekend."
      : "";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.visit_date) {
      toast.error("Please choose a Saturday or Sunday for your visit.");
      return;
    }
    if (dateError) {
      toast.error(dateError);
      return;
    }
    setSending(true);
    try {
      await axios.post(
        `${API}/visits`,
        { ...form, visitors: Number(form.visitors) },
        { withCredentials: true }
      );
      const { dow, day, mon } = fmtChip(form.visit_date);
      toast.success("Visit request received — we’ll confirm on your phone shortly.", {
        description: `${dow} ${day} ${mon} · ${form.visitors} guest${form.visitors > 1 ? "s" : ""}${
          form.meals ? " · meals" : ""
        }${form.night_stay ? " · night stay" : ""}`,
      });
      setForm(initial);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not book right now. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="visit" data-testid="visit-section" className="bg-parchment py-24 lg:py-36 border-y border-sand">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <Reveal>
            <Eyebrow tone="brand">08 — Visit the Farm</Eyebrow>
            <h2 className="mt-6 font-serif font-semibold text-4xl md:text-5xl leading-[1.05] tracking-tight text-pine">
              Spend a weekend
              <br />
              with our cows.
            </h2>
            <p className="mt-6 text-sage leading-relaxed max-w-md">
              Book a day at Khushalpura — walk the grazing fields, see the gobar gas plant, meet the
              calves and eat food cooked on the farm. Stay the night in our farm house if you like.
            </p>
            <p
              data-testid="visit-advance-note"
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-gold/15 border border-gold/40 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.22em] text-pine"
            >
              Advance booking compulsory
            </p>
            {!user && (
              <p className="mt-4 text-xs text-sage">
                <button
                  type="button"
                  data-testid="visit-signin-prompt"
                  onClick={() => openAuth("register")}
                  className="text-pine font-medium underline decoration-gold/60 underline-offset-4 hover:text-royal"
                >
                  Create a free account
                </button>{" "}
                to track your bookings — or book as a guest below.
              </p>
            )}
          </Reveal>

          <div className="mt-10 grid sm:grid-cols-2 gap-4">
            {RULES.map((r, i) => (
              <Reveal key={r.title} delay={0.08 + i * 0.06}>
                <div
                  data-testid={`visit-rule-${i}`}
                  className="h-full rounded-2xl bg-white border border-sand p-5 hover:border-gold transition-colors duration-500"
                >
                  <r.icon className="h-5 w-5 text-brand" />
                  <p className="mt-4 font-serif text-lg text-pine leading-tight">{r.title}</p>
                  <p className="mt-2 text-xs leading-relaxed text-sage">{r.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <form
              data-testid="visit-form"
              onSubmit={submit}
              className="rounded-3xl bg-white border border-sand p-7 md:p-9 grid gap-6"
            >
              <div>
                <span className={labelCls}>Choose a weekend day *</span>
                <div
                  data-testid="visit-date-chips"
                  className="mt-3 grid grid-cols-4 sm:grid-cols-8 gap-2"
                >
                  {weekends.map((ymd) => {
                    const c = fmtChip(ymd);
                    const active = form.visit_date === ymd;
                    return (
                      <button
                        key={ymd}
                        type="button"
                        data-testid={`visit-date-chip-${ymd}`}
                        onClick={() => setVal("visit_date", ymd)}
                        className={`rounded-2xl border px-2 py-3 text-center transition-colors duration-300 ${
                          active
                            ? "border-pine bg-pine text-cream"
                            : "border-sand bg-parchment text-coal hover:border-pine/50"
                        }`}
                      >
                        <span className={`block font-mono text-[9px] uppercase tracking-[0.2em] ${active ? "text-gold" : "text-sage"}`}>
                          {c.dow}
                        </span>
                        <span className="block font-serif text-2xl leading-none mt-1">{c.day}</span>
                        <span className={`block text-[10px] mt-1 ${active ? "text-cream/70" : "text-sage"}`}>{c.mon}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-3">
                  <label className="flex items-center gap-3 text-xs text-sage">
                    <span className="whitespace-nowrap">Or a later weekend:</span>
                    <input
                      data-testid="visit-input-date"
                      type="date"
                      min={minDate}
                      value={form.visit_date}
                      onChange={set("visit_date")}
                      className="rounded-lg border border-sand bg-white px-3 py-2 text-sm text-coal outline-none focus:border-pine"
                    />
                  </label>
                  {dateError && (
                    <p data-testid="visit-date-error" className="text-xs text-red-700">
                      {dateError}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid sm:grid-cols-3 gap-5">
                <label className="grid gap-2">
                  <span className={labelCls}>Name *</span>
                  <input
                    data-testid="visit-input-name"
                    className={inputCls}
                    required
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Your full name"
                  />
                </label>
                <label className="grid gap-2">
                  <span className={labelCls}>Phone *</span>
                  <input
                    data-testid="visit-input-phone"
                    className={inputCls}
                    required
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="+91"
                  />
                </label>
                <label className="grid gap-2">
                  <span className={labelCls}>Guests *</span>
                  <span className="relative">
                    <Users className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-sage" />
                    <input
                      data-testid="visit-input-visitors"
                      type="number"
                      min={1}
                      max={25}
                      required
                      className={`${inputCls} pl-11`}
                      value={form.visitors}
                      onChange={set("visitors")}
                    />
                  </span>
                </label>
              </div>

              <label className="grid gap-2">
                <span className={labelCls}>Email</span>
                <input
                  data-testid="visit-input-email"
                  type="email"
                  className={inputCls}
                  value={form.email}
                  onChange={set("email")}
                  placeholder="you@example.com"
                />
              </label>

              <div className="grid sm:grid-cols-2 gap-4">
                <Toggle
                  id="meals"
                  icon={Utensils}
                  label="Add farm meals"
                  hint="Breakfast, lunch & evening chai cooked at the farm"
                  checked={form.meals}
                  onChange={(v) => setVal("meals", v)}
                />
                <Toggle
                  id="night-stay"
                  icon={Moon}
                  label="Night stay at farm house"
                  hint="₹3,500 per night · up to 4 persons (2 adults + 2 kids)"
                  checked={form.night_stay}
                  onChange={(v) => setVal("night_stay", v)}
                />
              </div>

              <PriceCard form={form} />

              <label className="grid gap-2">
                <span className={labelCls}>Anything we should know?</span>
                <textarea
                  data-testid="visit-input-notes"
                  className={`${inputCls} min-h-[90px] resize-y`}
                  value={form.notes}
                  onChange={set("notes")}
                  placeholder="Children in the group, dietary needs, arrival time…"
                />
              </label>

              <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:justify-between">
                <button
                  data-testid="visit-form-submit-button"
                  type="submit"
                  disabled={sending}
                  className="inline-flex items-center gap-3 rounded-full bg-pine text-cream px-9 py-4 font-mono text-[11px] uppercase tracking-[0.22em] hover:bg-royal transition-colors duration-300 disabled:opacity-60"
                >
                  {sending ? "Booking…" : "Request Farm Visit"}
                </button>
                <p className="text-xs text-sage max-w-xs">
                  We confirm every booking by phone before your visit. Questions? Call{" "}
                  <a
                    data-testid="visit-phone-link"
                    href="tel:+918141261616"
                    className="text-pine font-medium whitespace-nowrap hover:text-royal transition-colors duration-300"
                  >
                    +91 81412 61616
                  </a>
                  . Farm house stays are subject to availability.
                </p>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
